/**
 * BookingStatusBadge Component
 *
 * Reusable badge that maps booking statusID to a color-coded label with icon.
 * Status codes:
 *   0  Pending    -> yellow
 *   1  Approved   -> blue
 *   2  Active     -> green
 *   3  Completed  -> teal
 *  -1  Rejected   -> red
 *  -2  Cancelled  -> gray
 *  -3  Overdue    -> orange
 *  -4  Incomplete -> pink
 */

import { Badge, type BadgeProps } from '@mantine/core';
import {
  IconClock,
  IconCheck,
  IconPlayerPlay,
  IconCircleCheck,
  IconX,
  IconBan,
  IconAlertTriangle,
  IconAlertCircle,
} from '@tabler/icons-react';

// ── Status configuration map ──────────────────────────────────────────
const STATUS_CONFIG: Record<
  number,
  { label: string; color: string; icon: React.ReactNode }
> = {
  0: {
    label: 'Pending',
    color: 'yellow',
    icon: <IconClock size={12} />,
  },
  1: {
    label: 'Approved',
    color: 'blue',
    icon: <IconCheck size={12} />,
  },
  2: {
    label: 'Active',
    color: 'green',
    icon: <IconPlayerPlay size={12} />,
  },
  3: {
    label: 'Completed',
    color: 'teal',
    icon: <IconCircleCheck size={12} />,
  },
  '-1': {
    label: 'Rejected',
    color: 'red',
    icon: <IconX size={12} />,
  },
  '-2': {
    label: 'Cancelled',
    color: 'gray',
    icon: <IconBan size={12} />,
  },
  '-3': {
    label: 'Overdue',
    color: 'orange',
    icon: <IconAlertTriangle size={12} />,
  },
  '-4': {
    label: 'Incomplete',
    color: 'pink',
    icon: <IconAlertCircle size={12} />,
  },
};

// ── Props ─────────────────────────────────────────────────────────────
interface BookingStatusBadgeProps extends Omit<BadgeProps, 'children'> {
  /** Numeric status ID or an object with statusID / name */
  status: number | { statusID: number; name?: string } | undefined | null;
  /** Whether to display the leading icon (default true) */
  showIcon?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────
export function BookingStatusBadge({
  status,
  showIcon = true,
  variant = 'light',
  size = 'sm',
  ...props
}: BookingStatusBadgeProps) {
  // Handle undefined / null
  if (status === undefined || status === null) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  // Normalise to number
  const statusID = typeof status === 'number' ? status : status.statusID;
  const statusName = typeof status === 'object' ? status.name : null;
  const config = STATUS_CONFIG[statusID];

  if (!config && !statusName) {
    return (
      <Badge variant={variant} size={size} color="gray" {...props}>
        Unknown
      </Badge>
    );
  }

  const label = statusName || config.label;
  const color = config?.color || 'gray';
  const icon = config?.icon;

  return (
    <Badge
      variant={variant}
      size={size}
      color={color}
      leftSection={showIcon && icon ? icon : undefined}
      {...props}
    >
      {label}
    </Badge>
  );
}

/** Helper: return the colour string for a given status ID */
export function getBookingStatusColor(statusID: number): string {
  return STATUS_CONFIG[statusID]?.color ?? 'gray';
}

/** Helper: return the label string for a given status ID */
export function getBookingStatusLabel(statusID: number): string {
  return STATUS_CONFIG[statusID]?.label ?? 'Unknown';
}
